import * as React from 'react';

import useModalAccessibility from '../../../Shared/hooks/useModalAccessibility';
import { hasSchoolOrDistrictPremium } from './userSubscription';

export const circleCheckSrc = `${window.postbuildEnv.CDN_URL}/images/pages/administrator/integrations/circle-check.svg`
export const circleCheckImg = <img alt="" src={circleCheckSrc} />

const ADMIN = 'admin'

// local roles
const NON_PREMIUM = 'teacherNonPremium'
const ADMIN_CL_PREMIUM = 'adminClPremium'
const TEACHER_CL_PREMIUM = 'teacherClPremium'

// buttons
const LEARN_MORE_BUTTON_TEXT = 'Learn more'
const PRIMARY_BUTTON_STYLE = "quill-button contained green medium focus-on-light"

// copy
const UNLOCK_CLASSLINK_WITH_SCHOOL_OR_DISTRICT_PREMIUM = 'Unlock ClassLink with School or District Premium'
const SEAMLESSLY_IMPORT_YOUR_CLASSLINK_ROSTERS = 'Seamlessly import your ClassLink rosters.'
const AUTOMATICALLY_CREATE_AND_SYNC_CLASSLINK_STUDENT_ACCOUNTS = 'Automatically create and sync ClassLink student accounts.'
const ACCESS_A_HOST_OF_OTHER_PREMIUM_BENEFITS = 'Access a host of other premium benefits, including priority technical support, enhanced reporting, and assistance from our professional learning team.'
const EXPLORE_PREMIUM = 'Explore premium'
const CONTACT_YOUR_ADMINISTRATOR = 'Contact your administrator'

// links
const RELATIVE_PREMIUM_HREF = '/premium'
const ROSTERING_HOWTO = 'https://support.quill.org/en/articles/11724850-school-district-premium-how-are-classes-rostered-with-classlink'
const HOW_DO_I_ROSTER = 'https://support.quill.org/en/articles/11321582-school-district-premium-how-do-i-set-up-classlink-rostering-for-my-school-district'

const classlinkStates = {
  [NON_PREMIUM]: {
    header: UNLOCK_CLASSLINK_WITH_SCHOOL_OR_DISTRICT_PREMIUM,
    paragraphCopy: 'Looking to streamline your teaching process with Quill’s ClassLink integration? Subscribe to School or District Premium to unlock:',
    checklistItems: [
      SEAMLESSLY_IMPORT_YOUR_CLASSLINK_ROSTERS,
      AUTOMATICALLY_CREATE_AND_SYNC_CLASSLINK_STUDENT_ACCOUNTS,
      ACCESS_A_HOST_OF_OTHER_PREMIUM_BENEFITS
    ],
    buttons: [
      <a className={PRIMARY_BUTTON_STYLE} href={RELATIVE_PREMIUM_HREF} key={EXPLORE_PREMIUM} rel="noopener noreferrer" target="_blank">{EXPLORE_PREMIUM}</a>,
    ]
  },

  [TEACHER_CL_PREMIUM]: {
    header: 'Get started with ClassLink',
    paragraphCopy: `Looking to streamline your teaching process with Quill’s ClassLink integration?
      Great news! Your Quill Premium subscription gives you access to the integration
      and benefits listed below.`,
    checklistItems: [
      SEAMLESSLY_IMPORT_YOUR_CLASSLINK_ROSTERS,
      AUTOMATICALLY_CREATE_AND_SYNC_CLASSLINK_STUDENT_ACCOUNTS,
      ACCESS_A_HOST_OF_OTHER_PREMIUM_BENEFITS
    ],
    buttons: [
      <a className={PRIMARY_BUTTON_STYLE} href={ROSTERING_HOWTO} key={CONTACT_YOUR_ADMINISTRATOR} rel="noopener noreferrer" target="_blank">{LEARN_MORE_BUTTON_TEXT}</a>
    ]
  },// this is also TEACHER_NON_CL_PREMIUM

  [ADMIN_CL_PREMIUM]: {
    header: 'Get started with ClassLink',
    paragraphCopy: `Looking to streamline your teaching process with Quill’s ClassLink integration?
      Great news! Your Quill Premium subscription gives you access to the integration
      and benefits listed below.`,
    checklistItems: [
      SEAMLESSLY_IMPORT_YOUR_CLASSLINK_ROSTERS,
      AUTOMATICALLY_CREATE_AND_SYNC_CLASSLINK_STUDENT_ACCOUNTS,
      ACCESS_A_HOST_OF_OTHER_PREMIUM_BENEFITS
    ],
    buttons: [
      <a className={PRIMARY_BUTTON_STYLE} href={HOW_DO_I_ROSTER} key={CONTACT_YOUR_ADMINISTRATOR} rel="noopener noreferrer" target="_blank">{LEARN_MORE_BUTTON_TEXT}</a>
    ]
  }, // same as ADMIN_NON_CL_PREMIUM

}

const computeClasslinkState = (user) => {
  const isPremium = hasSchoolOrDistrictPremium(user)
  if (!isPremium) {
    return NON_PREMIUM
  }
  if (user.role === ADMIN) {
    return ADMIN_CL_PREMIUM
  }
  return TEACHER_CL_PREMIUM
}

const ClasslinkModal = ({ close, user }) => {
  const { modalRef } = useModalAccessibility(close);

  const classlinkState = computeClasslinkState(user);
  return (
    <div className="modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="ClasslinkModalTitle"
        aria-modal="true"
        className="classlink-modal quill-modal modal-body"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >
        <div>
          <h3 className="title" id="ClasslinkModalTitle">{classlinkStates[classlinkState].header}</h3>
        </div>
        <p>{classlinkStates[classlinkState].paragraphCopy}</p>
        <ul>
          {classlinkStates[classlinkState].checklistItems.map(checklistItem => (
            <li key={checklistItem}>{circleCheckImg}{checklistItem}</li>
          ))}
        </ul>
        <div className="form-buttons">
          <button
            className="quill-button outlined green medium focus-on-light"
            onClick={close}
            type="button"
          >
            Cancel
          </button>
          <div className="right-aligned-buttons">
            {classlinkStates[classlinkState].buttons}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClasslinkModal;
